// GalleryPortal.jsx
import ReactDOM from 'react-dom';
import React, { useState, useEffect, useRef } from 'react';
import PropertyGallery from './Gallery';
import { withRouter } from 'react-router-dom';

let openGalleryFn = () => {};

const OTHER_VISIBLE_POPUP_SELECTOR =
  '[class*="__modal"][class*="__open"], [data-modal-open="true"]';

const GalleryPortal = ({ history }) => {
  const [galleryProps, setGalleryProps] = useState(null);
  const initialPopupsRef = useRef(new Set());

  const close = () => setGalleryProps(null);

  // Close on route change (unchanged)
  useEffect(() => {
    if (!history) return;
    const unlisten = history.listen(close);
    return () => unlisten();
  }, [history]);

  // Only react to *new* popups created after the gallery opens
  useEffect(() => {
    if (!galleryProps) return;

    // Snapshot currently open popups so we can ignore them
    initialPopupsRef.current = new Set(
      Array.from(document.querySelectorAll(OTHER_VISIBLE_POPUP_SELECTOR))
    );

    const self = () => document.querySelector('[data-gallery-portal="true"]');

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        // React to nodes added
        for (const node of record.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          // If a new popup appears
          if (node.matches?.(OTHER_VISIBLE_POPUP_SELECTOR)) {
            if (!initialPopupsRef.current.has(node)) {
              const portalRoot = self();
              if (portalRoot && !portalRoot.contains(node) && !node.contains(portalRoot)) {
                close(); // another, newer popup took over → close gallery
                return;
              }
            }
          }
        }

        // Also react to class changes that toggle an existing element "open"
        if (
          record.type === 'attributes' &&
          record.target instanceof HTMLElement &&
          record.attributeName === 'class'
        ) {
          const el = record.target;
          const nowOpen = el.matches?.(OTHER_VISIBLE_POPUP_SELECTOR);
          const wasKnown = initialPopupsRef.current.has(el);

          if (nowOpen && !wasKnown) {
            const portalRoot = self();
            if (portalRoot && !portalRoot.contains(el) && !el.contains(portalRoot)) {
              close();
              return;
            }
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, [galleryProps]);

  openGalleryFn = (props) => setGalleryProps({ ...props });

  if (!galleryProps) return null;

  return ReactDOM.createPortal(
    <div data-gallery-portal="true">
      <PropertyGallery {...galleryProps} closePopup={close} />
    </div>,
    document.body
  );
};

export const openGallery = (props) => openGalleryFn(props);
export default withRouter(GalleryPortal);